// http-service.js
import axios, { type AxiosRequestConfig } from 'axios';
import { Response, COMMAND_REDIRECT } from '../parsers/response.js';

// Extend Window interface to include custom properties
declare global {
  interface Window {
    csrfToken?: string;
    REQUEST_PAYLOAD?: any;
    SECTION_SERVICE_PAYLOAD?: any;
  }
}

// Shared utility functions
const getBaseUrl = () => {
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  const port = window.location.port;
  let fullHost = `${protocol}//${hostname}${port ? `:${port}` : ''}`;
  return fullHost;
};

// Get current CSRF token - function instead of static value
const getCsrfToken = () => {
  return window.csrfToken || '';
};

// Get headers dynamically - function instead of static object
const getHeaders = () => {
  return {
    'X-CSRFToken': getCsrfToken(),
    'X-Requested-With': 'XMLHttpRequest',
    'Content-Type': 'application/json',
  };
};

const handleRedirect = (url: string) => {
  const currentUrl = new URL(window.location.href);
  const targetUrl = new URL(url, window.location.origin);
  
  if (targetUrl.pathname === currentUrl.pathname && targetUrl.search === currentUrl.search) {
    if (targetUrl.hash !== currentUrl.hash) {
      history.replaceState(null, '', targetUrl.pathname + targetUrl.search + targetUrl.hash);
      window.dispatchEvent(new HashChangeEvent('hashchange'));
      window.location.href = url;
    } else {
      console.log("Hash is the same, no action needed");
    }
  } else {
    window.location.href = url;
  }
};

export const call = async (service: any, params: any, parseResponse = true) => {
  if (window.NO_NETWORKING) {
    const payload = window.SECTION_SERVICE_PAYLOAD || {};
    const response = new Response(payload);
    console.warn('window.NO_NETWORKING=true, using offline data');

    if (response.containsServiceCommand(COMMAND_REDIRECT)) {
      const redirect = response.parse(COMMAND_REDIRECT, false, false);
      console.log('Offline response contains redirect command, handling redirect to:', redirect.url);
      handleRedirect(redirect.url);
    }

    return response;
  }

  const mergedParams = {
    ...(typeof params === 'object' ? params : {}),
    ...(typeof window.REQUEST_PAYLOAD === 'object' ? window.REQUEST_PAYLOAD : {}),
  };

  // Get fresh headers for each request
  const config: AxiosRequestConfig = {
    method: 'POST',
    url: `${getBaseUrl()}/GOTO/${service}/`,
    headers: getHeaders(), // Call function to get current headers
    data: mergedParams,
    withCredentials: true,
  };

  try {
    const req = await axios(config);
    const requestResponse = req.data;

    if (parseResponse) {
      const response = new Response(requestResponse);
      if (response.containsServiceCommand(COMMAND_REDIRECT)) {
        const redirect = response.parse(COMMAND_REDIRECT, false, false);
        handleRedirect(redirect.url);
      }
      return response;
    } else {
      return requestResponse;
    }
  } catch (error) {
    console.error('An error occurred while processing the response:', error);
    throw error;
  }
};

export const callServiceParams = async (service: any, params: { [key: string]: any; }) => {
  return await call(service, params);
};

export const withHttpMixin = (BaseClass: any) =>
  class extends BaseClass {
    get baseUrl() {
      return getBaseUrl();
    }

    // Get current CSRF token dynamically
    get currentCsrfToken() {
      return getCsrfToken();
    }

    // Get headers dynamically
    get headers() {
      return getHeaders();
    }

    async call(service: any, params: any, parseResponse = true) {
      return await call(service, params, parseResponse);
    }

    async callServiceParams(service: any, params: any) {
      return await callServiceParams(service, params);
    }

    sendRequest(config: AxiosRequestConfig<any>) {
      // Ensure headers are fresh before sending
      if (!config.headers) {
        config.headers = {};
      }
      // Always use current CSRF token
      config.headers['X-CSRFToken'] = getCsrfToken();
      if (!config.headers['X-Requested-With']) {
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
      }
      if (!config.headers['Content-Type']) {
        config.headers['Content-Type'] = 'application/json';
      }
      return axios(config);
    }
  };